import { useState, useEffect, useCallback } from "react";
import { getBHLocationData } from "../services/bhDataService";

/**
 * Hook para cargar datos del BH Dashboard por location.
 * Carga todo de un solo JSON pero setea loading false por sección.
 *
 * @param {string|null} locationKey - Key de la location seleccionada
 * @returns {{ overview, riskStratification, substanceUse, erUtilization, unmanagedPatients, carePrograms, taskStats, loading, error }}
 */
export default function useBHData(locationKey) {
  const [overview, setOverview] = useState(null);
  const [riskStratification, setRiskStratification] = useState(null);
  const [substanceUse, setSubstanceUse] = useState(null);
  const [erUtilization, setERUtilization] = useState(null);
  const [unmanagedPatients, setUnmanagedPatients] = useState(null);
  const [carePrograms, setCarePrograms] = useState(null);
  const [taskStats, setTaskStats] = useState(null);

  const [loading, setLoading] = useState({
    overview: true,
    risk: true,
    substanceUse: true,
    erUtilization: true,
    unmanaged: true,
    carePrograms: true,
  });

  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!locationKey) return;

    // Reset loading states
    setLoading({
      overview: true,
      risk: true,
      substanceUse: true,
      erUtilization: true,
      unmanaged: true,
      carePrograms: true,
    });
    setError(null);

    try {
      const data = await getBHLocationData(locationKey);

      setOverview(data.overview);
      setLoading((prev) => ({ ...prev, overview: false }));

      setRiskStratification(data.riskStratification);
      setLoading((prev) => ({ ...prev, risk: false }));

      setSubstanceUse(data.substanceUse);
      setLoading((prev) => ({ ...prev, substanceUse: false }));

      setERUtilization(data.erUtilization);
      setLoading((prev) => ({ ...prev, erUtilization: false }));

      setUnmanagedPatients(data.unmanagedPatients);
      setLoading((prev) => ({ ...prev, unmanaged: false }));

      setCarePrograms(data.carePrograms);
      setLoading((prev) => ({ ...prev, carePrograms: false }));

      setTaskStats(data.taskStats);
    } catch (err) {
      console.error("Error loading BH data:", err);
      setError(err);
      setLoading({
        overview: false,
        risk: false,
        substanceUse: false,
        erUtilization: false,
        unmanaged: false,
        carePrograms: false,
      });
    }
  }, [locationKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    overview,
    riskStratification,
    substanceUse,
    erUtilization,
    unmanagedPatients,
    carePrograms,
    taskStats,
    loading,
    error,
  };
}
